import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Col,
  Row,
  Form,
  Input,
  Select,
  Button,
  AutoComplete,
  Slider,
  Typography,
  Divider,
  Space,
  message,
  Modal,
  Progress,
  Image,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  PlayCircleOutlined,
  PauseCircleOutlined,
  DownloadOutlined,
  EyeOutlined,
  VideoCameraOutlined,
  FileImageOutlined,
} from "@ant-design/icons";
import debounce from "lodash/debounce";
import { saveAs } from "file-saver";

import styles from "./index.module.css";
import { getApi } from "../../../helpers";

const { Title, Text } = Typography;

// Default theme configurations
const THEMES = {
  wowWednesday: {
    headerText: "WOW!",
    subHeaderText: "WEDNESDAY",
    headerGradient: ["#1e88e5", "#42a5f5"],
    backgroundColor: "#f0f4ff",
    saveBadgeColor: "#ef4444",
    priceButtonColor: "#22c55e",
    categoryBorderColor: "#3b82f6",
    headerTextColor: "#1e88e5",
    productNameColor: "#1f2937",
    categoryTextColor: "#3b82f6",
  },
  superSaturday: {
    headerText: "SUPER",
    subHeaderText: "SATURDAY",
    headerGradient: ["#9333ea", "#c084fc"],
    backgroundColor: "#faf5ff",
    saveBadgeColor: "#f97316",
    priceButtonColor: "#8b5cf6",
    categoryBorderColor: "#9333ea",
    headerTextColor: "#9333ea",
    productNameColor: "#1f2937",
    categoryTextColor: "#9333ea",
  },
  freshFriday: {
    headerText: "FRESH",
    subHeaderText: "FRIDAY",
    headerGradient: ["#16a34a", "#4ade80"],
    backgroundColor: "#f0fdf4",
    saveBadgeColor: "#dc2626",
    priceButtonColor: "#16a34a",
    categoryBorderColor: "#16a34a",
    headerTextColor: "#16a34a",
    productNameColor: "#1f2937",
    categoryTextColor: "#16a34a",
  },
  custom: {
    headerText: "YOUR",
    subHeaderText: "PROMO",
    headerGradient: ["#1e88e5", "#42a5f5"],
    backgroundColor: "#ffffff",
    saveBadgeColor: "#ef4444",
    priceButtonColor: "#22c55e",
    categoryBorderColor: "#3b82f6",
    headerTextColor: "#1e88e5",
    productNameColor: "#1f2937",
    categoryTextColor: "#3b82f6",
  },
};

export default function TVAdEditor({ onExport }) {
  const [form] = Form.useForm();
  const previewRef = useRef(null);
  const animationRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);

  // Theme and styling state
  const [selectedTheme, setSelectedTheme] = useState("wowWednesday");
  const [customTheme, setCustomTheme] = useState(THEMES.wowWednesday);

  // Product state
  const [products, setProducts] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Animation state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [animationSpeed, setAnimationSpeed] = useState(3000); // ms per product

  // Category state
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordedChunks, setRecordedChunks] = useState([]);

  useEffect(() => {
    fetchCategories();
  }, []);

  // Animation effect
  useEffect(() => {
    if (isPlaying && products.length > 1) {
      animationRef.current = setInterval(() => {
        setCurrentProductIndex((prev) => (prev + 1) % products.length);
      }, animationSpeed);
    }
    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
      }
    };
  }, [isPlaying, products.length, animationSpeed]);

  const fetchCategories = async () => {
    const res = await getApi({ size: 100 }, "getCategory");
    if (res.data) {
      setCategories(res.data);
    }
  };

  const searchProducts = async (keyword) => {
    if (!keyword || keyword.length < 2) return;
    setLoading(true);
    setProductOptions([{ label: "Searching...", disabled: true }]);
    const res = await getApi({ size: 50, keyword }, "getVariants");
    console.log("Search API response:", res); // Debug log
    if (res.data && res.data.length) {
      setProductOptions(
        res.data.map((v) => {
          // Get first image URL from various possible locations
          let imgUrl = "";
          if (v.images && v.images.length > 0) {
            imgUrl = v.images[0].url || v.images[0];
          } else if (v.variant_image) {
            imgUrl = v.variant_image;
          } else if (v.product_image) {
            imgUrl = v.product_image;
          }

          return {
            value: v.product_name + " - " + v.variant_name,
            key: v.variant_id,
            pid: v.product_id,
            imgUrl: imgUrl,
            data: v,
          };
        })
      );
    } else {
      setProductOptions([{ label: "No products found", disabled: true }]);
    }
    setLoading(false);
  };

  const debouncedSearch = useCallback(
    debounce((value) => searchProducts(value), 800),
    []
  );

  const handleProductSelect = (value, option) => {
    if (option.data) {
      // Use the pre-processed imgUrl from search, or try to get it from data
      let imageUrl = option.imgUrl || "";
      if (!imageUrl) {
        if (option.data.images && option.data.images.length > 0) {
          imageUrl = option.data.images[0].url || option.data.images[0];
        } else if (option.data.variant_image) {
          imageUrl = option.data.variant_image;
        } else if (option.data.product_image) {
          imageUrl = option.data.product_image;
        } else if (option.data.image) {
          imageUrl = option.data.image;
        }
      }

      // Get MRP and selling price, ensuring they are numbers
      const mrp = parseFloat(option.data.mrp) || 0;
      const sellingPrice = parseFloat(
        option.data.selling_price ||
        option.data.price ||
        option.data.mrp
      ) || mrp;

      // Calculate savings (MRP - Selling Price)
      const savings = Math.max(0, Math.round(mrp - sellingPrice));

      const newProduct = {
        id: option.data.variant_id,
        productId: option.data.product_id,
        name: option.data.product_name,
        variantName: option.data.variant_name,
        image: imageUrl,
        price: sellingPrice,
        mrp: mrp,
        savings: savings,
      };

      console.log("Product data from API:", option.data); // Debug log
      console.log("Product added:", newProduct); // Debug log
      console.log("MRP:", mrp, "Selling Price:", sellingPrice, "Savings:", savings); // Debug log
      setProducts([...products, newProduct]);
      form.setFieldValue("productSearch", "");
    }
  };

  const removeProduct = (index) => {
    const newProducts = [...products];
    newProducts.splice(index, 1);
    setProducts(newProducts);
    if (currentProductIndex >= newProducts.length) {
      setCurrentProductIndex(Math.max(0, newProducts.length - 1));
    }
  };

  const handleThemeChange = (themeName) => {
    setSelectedTheme(themeName);
    if (themeName !== "custom") {
      setCustomTheme(THEMES[themeName]);
    }
  };

  // Draw a single frame to canvas
  const drawFrameToCanvas = (canvas, productIndex) => {
    const ctx = canvas.getContext("2d");
    const width = 1080;
    const height = 1920;
    const product = products[productIndex];

    // Clear canvas
    ctx.fillStyle = customTheme.backgroundColor;
    ctx.fillRect(0, 0, width, height);

    // Draw sunburst background
    ctx.save();
    ctx.translate(width / 2, 400);
    for (let i = 0; i < 36; i++) {
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(width, 0);
      ctx.rotate((Math.PI * 2) / 36);
      ctx.fillStyle = i % 2 === 0 ? "rgba(255,255,255,0.8)" : "rgba(230,240,255,0.6)";
      ctx.fill();
    }
    ctx.restore();

    // Draw header text
    ctx.textAlign = "center";
    ctx.fillStyle = customTheme.headerTextColor || customTheme.headerGradient[0];
    ctx.font = "bold 180px Impact, Arial Black, sans-serif";
    ctx.fillText(customTheme.headerText, width / 2, 280);
    ctx.font = "bold 120px Impact, Arial Black, sans-serif";
    ctx.fillText(customTheme.subHeaderText, width / 2, 420);

    // Draw category label
    if (selectedCategory) {
      ctx.strokeStyle = customTheme.categoryBorderColor;
      ctx.lineWidth = 6;
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      const catText = selectedCategory.toUpperCase();
      const catWidth = ctx.measureText(catText).width + 100;
      const catX = (width - catWidth) / 2;
      ctx.beginPath();
      ctx.roundRect(catX, 480, catWidth, 80, 40);
      ctx.fill();
      ctx.stroke();
      ctx.fillStyle = customTheme.categoryTextColor || customTheme.categoryBorderColor;
      ctx.font = "bold 40px Arial, sans-serif";
      ctx.fillText(catText, width / 2, 535);
    }

    // Draw product card
    if (product) {
      const cardX = 100;
      const cardY = 600;
      const cardW = width - 200;
      const cardH = 1100;

      // Card background
      ctx.fillStyle = "rgba(255,255,255,0.95)";
      ctx.beginPath();
      ctx.roundRect(cardX, cardY, cardW, cardH, 40);
      ctx.fill();
      ctx.shadowColor = "rgba(0,0,0,0.1)";
      ctx.shadowBlur = 40;

      // Save badge
      if (product.savings > 0) {
        ctx.fillStyle = customTheme.saveBadgeColor;
        ctx.beginPath();
        ctx.roundRect(cardX + 30, cardY - 30, 280, 80, 20);
        ctx.fill();
        ctx.fillStyle = "white";
        ctx.font = "bold 40px Arial, sans-serif";
        ctx.textAlign = "left";
        ctx.fillText(`SAVE ₹${product.savings}`, cardX + 60, cardY + 25);
      }

      // Product name
      ctx.fillStyle = customTheme.productNameColor || "#1f2937";
      ctx.font = "bold 56px Arial, sans-serif";
      ctx.textAlign = "center";
      const productLabel = `${product.name} ${product.variantName}`;
      ctx.fillText(productLabel, width / 2, cardY + cardH - 200, cardW - 80);

      // Price button
      const priceY = cardY + cardH - 100;
      ctx.fillStyle = customTheme.priceButtonColor;
      ctx.beginPath();
      ctx.roundRect(width / 2 - 150, priceY - 40, 300, 80, 40);
      ctx.fill();
      ctx.fillStyle = "white";
      ctx.font = "bold 48px Arial, sans-serif";
      ctx.fillText(`₹${product.price}`, width / 2 - 30, priceY + 15);
      if (product.mrp > product.price) {
        ctx.font = "36px Arial, sans-serif";
        ctx.fillStyle = "rgba(255,255,255,0.7)";
        ctx.fillText(`₹${product.mrp}`, width / 2 + 90, priceY + 15);
        // Strikethrough
        ctx.strokeStyle = "rgba(255,255,255,0.7)";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(width / 2 + 55, priceY + 5);
        ctx.lineTo(width / 2 + 130, priceY + 5);
        ctx.stroke();
      }
    }

    // Draw carousel indicators
    if (products.length > 1) {
      const indicatorY = 1780;
      const indicatorSpacing = 30;
      const startX = width / 2 - ((products.length - 1) * indicatorSpacing) / 2;
      products.forEach((_, idx) => {
        ctx.beginPath();
        ctx.arc(startX + idx * indicatorSpacing, indicatorY, idx === productIndex ? 15 : 10, 0, Math.PI * 2);
        ctx.fillStyle = idx === productIndex ? customTheme.headerGradient[0] : "rgba(0,0,0,0.2)";
        ctx.fill();
      });
    }
  };

  // Export as video using MediaRecorder
  const startRecording = async () => {
    if (products.length === 0) {
      message.warning("Please add at least one product to record");
      return;
    }

    setIsRecording(true);
    setRecordedChunks([]);
    setExportProgress(0);

    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1920;
    canvasRef.current = canvas;

    const stream = canvas.captureStream(30);
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: "video/webm;codecs=vp9",
      videoBitsPerSecond: 8000000,
    });
    mediaRecorderRef.current = mediaRecorder;

    const chunks = [];
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };

    mediaRecorder.onstop = () => {
      const blob = new Blob(chunks, { type: "video/webm" });
      saveAs(blob, `tv-ad-${Date.now()}.webm`);
      setIsRecording(false);
      setExportProgress(100);
      message.success("Video exported successfully!");
    };

    mediaRecorder.start(100);

    // Record all products in sequence
    const totalDuration = products.length * animationSpeed;
    let elapsed = 0;
    const frameInterval = 33; // ~30fps

    const recordFrame = () => {
      if (elapsed >= totalDuration) {
        mediaRecorder.stop();
        return;
      }

      const productIdx = Math.floor(elapsed / animationSpeed) % products.length;
      drawFrameToCanvas(canvas, productIdx);

      setExportProgress(Math.round((elapsed / totalDuration) * 100));
      elapsed += frameInterval;

      setTimeout(recordFrame, frameInterval);
    };

    // Load product images before recording
    message.info("Loading images and starting recording...");
    setTimeout(recordFrame, 500);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  // Export individual frames as images
  const exportFrames = async () => {
    if (products.length === 0) {
      message.warning("Please add at least one product to export");
      return;
    }

    setIsExporting(true);
    setExportProgress(0);

    const canvas = document.createElement("canvas");
    canvas.width = 1080;
    canvas.height = 1920;

    for (let i = 0; i < products.length; i++) {
      drawFrameToCanvas(canvas, i);

      // Convert to blob and download
      const dataUrl = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.download = `tv-ad-frame-${i + 1}.png`;
      link.href = dataUrl;
      link.click();

      setExportProgress(Math.round(((i + 1) / products.length) * 100));
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setIsExporting(false);
    message.success(`Exported ${products.length} frames successfully!`);
  };

  // Export configuration JSON
  const exportConfig = () => {
    const config = {
      theme: customTheme,
      products: products,
      category: selectedCategory,
      animationSpeed: animationSpeed,
      dimensions: { width: 1080, height: 1920 },
    };

    const blob = new Blob([JSON.stringify(config, null, 2)], {
      type: "application/json",
    });
    saveAs(blob, `tv-ad-config-${Date.now()}.json`);
    message.success("Configuration exported!");
  };

  const handleExportMP4 = () => {
    if (products.length === 0) {
      message.warning("Please add at least one product to export");
      return;
    }
    setExportModalVisible(true);
  };

  const currentProduct = products[currentProductIndex];

  return (
    <div className={styles.editorContainer}>
      <Row gutter={24}>
        {/* Left Panel - Editor Controls */}
        <Col xs={24} lg={12}>
          <div className={styles.controlPanel}>
            <Title level={4}>TV Ad Editor</Title>

            {/* Theme Selection */}
            <div className={styles.section}>
              <Text strong>Select Theme</Text>
              <Select
                value={selectedTheme}
                onChange={handleThemeChange}
                style={{ width: "100%", marginTop: 8 }}
              >
                <Select.Option value="wowWednesday">WOW! Wednesday</Select.Option>
                <Select.Option value="superSaturday">Super Saturday</Select.Option>
                <Select.Option value="freshFriday">Fresh Friday</Select.Option>
                <Select.Option value="custom">Custom Theme</Select.Option>
              </Select>
            </div>

            {/* Custom Theme Options */}
            <div className={styles.section}>
              <Text strong>Header Text</Text>
              <Row gutter={8} style={{ marginTop: 8 }}>
                <Col span={12}>
                  <Input
                    placeholder="Main Text"
                    value={customTheme.headerText}
                    onChange={(e) =>
                      setCustomTheme({ ...customTheme, headerText: e.target.value })
                    }
                  />
                </Col>
                <Col span={12}>
                  <Input
                    placeholder="Sub Text"
                    value={customTheme.subHeaderText}
                    onChange={(e) =>
                      setCustomTheme({ ...customTheme, subHeaderText: e.target.value })
                    }
                  />
                </Col>
              </Row>
            </div>

            {/* Color Customization */}
            <div className={styles.section}>
              <Text strong>Colors</Text>
              <Row gutter={[8, 8]} style={{ marginTop: 8 }}>
                <Col span={8}>
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Background</Text>
                  <input
                    type="color"
                    value={customTheme.backgroundColor}
                    onChange={(e) =>
                      setCustomTheme({
                        ...customTheme,
                        backgroundColor: e.target.value,
                      })
                    }
                    className={styles.colorInput}
                  />
                </Col>
                <Col span={8}>
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Save Badge</Text>
                  <input
                    type="color"
                    value={customTheme.saveBadgeColor}
                    onChange={(e) =>
                      setCustomTheme({
                        ...customTheme,
                        saveBadgeColor: e.target.value,
                      })
                    }
                    className={styles.colorInput}
                  />
                </Col>
                <Col span={8}>
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Price Button</Text>
                  <input
                    type="color"
                    value={customTheme.priceButtonColor}
                    onChange={(e) =>
                      setCustomTheme({
                        ...customTheme,
                        priceButtonColor: e.target.value,
                      })
                    }
                    className={styles.colorInput}
                  />
                </Col>
              </Row>
              <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>Text Colors</Text>
              <Row gutter={[8, 8]} style={{ marginTop: 4 }}>
                <Col span={8}>
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Header</Text>
                  <input
                    type="color"
                    value={customTheme.headerTextColor || customTheme.headerGradient[0]}
                    onChange={(e) =>
                      setCustomTheme({
                        ...customTheme,
                        headerTextColor: e.target.value,
                        headerGradient: [e.target.value, customTheme.headerGradient[1]],
                      })
                    }
                    className={styles.colorInput}
                  />
                </Col>
                <Col span={8}>
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Product Name</Text>
                  <input
                    type="color"
                    value={customTheme.productNameColor || "#1f2937"}
                    onChange={(e) =>
                      setCustomTheme({
                        ...customTheme,
                        productNameColor: e.target.value,
                      })
                    }
                    className={styles.colorInput}
                  />
                </Col>
                <Col span={8}>
                  <Text type="secondary" style={{ fontSize: 12, display: "block" }}>Category</Text>
                  <input
                    type="color"
                    value={customTheme.categoryTextColor || customTheme.categoryBorderColor}
                    onChange={(e) =>
                      setCustomTheme({
                        ...customTheme,
                        categoryTextColor: e.target.value,
                      })
                    }
                    className={styles.colorInput}
                  />
                </Col>
              </Row>
            </div>

            <Divider />

            {/* Category Selection */}
            <div className={styles.section}>
              <Text strong>Category Label</Text>
              <Select
                placeholder="Select category"
                value={selectedCategory}
                onChange={setSelectedCategory}
                style={{ width: "100%", marginTop: 8 }}
                allowClear
              >
                {categories.map((cat) => (
                  <Select.Option key={cat.category_id} value={cat.name}>
                    {cat.name}
                  </Select.Option>
                ))}
              </Select>
            </div>

            <Divider />

            {/* Product Search */}
            <div className={styles.section}>
              <Text strong>Add Products</Text>
              <Form form={form}>
                <Form.Item name="productSearch" style={{ marginTop: 8, marginBottom: 0 }}>
                  <AutoComplete
                    loading={loading}
                    options={productOptions}
                    onSelect={handleProductSelect}
                    onSearch={(value) => value.length > 1 && debouncedSearch(value)}
                    placeholder="Search products..."
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Form>
            </div>

            {/* Products List */}
            <div className={styles.productList}>
              {products.map((product, index) => (
                <div key={product.id} className={styles.productItem}>
                  <Image
                    src={product.image}
                    alt={product.name}
                    className={styles.productThumb}
                    preview={false}
                    width={40}
                    height={40}
                    fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIGZpbGw9IiNmNWY1ZjUiLz48L3N2Zz4="
                    style={{ objectFit: "contain", borderRadius: 4 }}
                  />
                  <div className={styles.productInfo}>
                    <Text ellipsis style={{ width: 120 }}>
                      {product.name}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {product.variantName}
                    </Text>
                  </div>
                  <div className={styles.productPriceInfo}>
                    <Text style={{ fontSize: 12, color: "#52c41a", fontWeight: 600 }}>
                      ₹{product.price}
                    </Text>
                    {product.savings > 0 && (
                      <Text type="secondary" style={{ fontSize: 10 }}>
                        Save ₹{product.savings}
                      </Text>
                    )}
                  </div>
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeProduct(index)}
                  />
                </div>
              ))}
              {products.length === 0 && (
                <div className={styles.emptyProducts}>
                  <Text type="secondary">No products added yet</Text>
                </div>
              )}
            </div>

            <Divider />

            {/* Animation Controls */}
            <div className={styles.section}>
              <Text strong>Animation Speed</Text>
              <Slider
                min={1000}
                max={10000}
                step={500}
                value={animationSpeed}
                onChange={setAnimationSpeed}
                marks={{
                  1000: "1s",
                  5000: "5s",
                  10000: "10s",
                }}
              />
            </div>

            {/* Action Buttons */}
            <div className={styles.actionButtons}>
              <Space wrap>
                <Button
                  type={isPlaying ? "default" : "primary"}
                  icon={isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                  onClick={() => setIsPlaying(!isPlaying)}
                  disabled={products.length < 2}
                >
                  {isPlaying ? "Pause" : "Preview"}
                </Button>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleExportMP4}
                  style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                >
                  Export
                </Button>
              </Space>
            </div>

            {/* Recording Progress */}
            {(isRecording || isExporting) && (
              <div style={{ marginTop: 16 }}>
                <Progress percent={exportProgress} status="active" />
                <Text type="secondary" style={{ display: "block", textAlign: "center", marginTop: 8 }}>
                  {isRecording ? "Recording video..." : "Exporting frames..."}
                </Text>
                {isRecording && (
                  <Button
                    danger
                    size="small"
                    onClick={stopRecording}
                    style={{ marginTop: 8, display: "block", margin: "8px auto 0" }}
                  >
                    Stop Recording
                  </Button>
                )}
              </div>
            )}
          </div>
        </Col>

        {/* Right Panel - TV Preview */}
        <Col xs={24} lg={12}>
          <div className={styles.previewContainer}>
            <Text strong style={{ display: "block", textAlign: "center", marginBottom: 16 }}>
              TV Screen Preview (Vertical)
            </Text>

            <div
              className={styles.tvFrame}
              style={{ backgroundImage: `url(/images/tv-preview-bg.png)` }}
            >
              <div className={styles.tvBezel}>
                <div
                  ref={previewRef}
                  className={styles.tvScreen}
                  style={{ backgroundColor: customTheme.backgroundColor }}
                >
                  {/* Sunburst Background */}
                  <div className={styles.sunburstBg} />

                  {/* Header Section */}
                  <div className={styles.headerSection}>
                    <div
                      className={styles.headerText}
                      style={{
                        color: customTheme.headerTextColor || customTheme.headerGradient[0],
                      }}
                    >
                      {customTheme.headerText}
                    </div>
                    <div
                      className={styles.subHeaderText}
                      style={{
                        color: customTheme.headerTextColor || customTheme.headerGradient[0],
                      }}
                    >
                      {customTheme.subHeaderText}
                    </div>
                  </div>

                  {/* Category Label */}
                  {selectedCategory && (
                    <div
                      className={styles.categoryLabel}
                      style={{
                        borderColor: customTheme.categoryBorderColor,
                        color: customTheme.categoryTextColor || customTheme.categoryBorderColor,
                      }}
                    >
                      {selectedCategory.toUpperCase()}
                    </div>
                  )}

                  {/* Product Card */}
                  {currentProduct ? (
                    <div className={styles.productCard}>
                      {/* Save Badge */}
                      {currentProduct.savings > 0 && (
                        <div
                          className={styles.saveBadge}
                          style={{ backgroundColor: customTheme.saveBadgeColor }}
                        >
                          SAVE ₹{currentProduct.savings}
                        </div>
                      )}

                      {/* Product Image */}
                      <div className={styles.productImageContainer}>
                        <Image
                          src={currentProduct.image}
                          alt={currentProduct.name}
                          className={styles.productImage}
                          preview={false}
                          fallback="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTQwIiBoZWlnaHQ9IjE0MCIgdmlld0JveD0iMCAwIDE0MCAxNDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjE0MCIgaGVpZ2h0PSIxNDAiIGZpbGw9IiNmNWY1ZjUiLz48dGV4dCB4PSI3MCIgeT0iNzUiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiNiZmJmYmYiIGZvbnQtc2l6ZT0iMTIiPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg=="
                          style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                        />
                      </div>

                      {/* Product Name */}
                      <div
                        className={styles.productName}
                        style={{ color: customTheme.productNameColor || "#1f2937" }}
                      >
                        {currentProduct.name} {currentProduct.variantName}
                      </div>

                      {/* Price Button */}
                      <div
                        className={styles.priceButton}
                        style={{ backgroundColor: customTheme.priceButtonColor }}
                      >
                        <span className={styles.currentPrice}>₹{currentProduct.price}</span>
                        {currentProduct.mrp > currentProduct.price && (
                          <span className={styles.originalPrice}>₹{currentProduct.mrp}</span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className={styles.emptyProductCard}>
                      <PlusOutlined style={{ fontSize: 32, color: "#ccc" }} />
                      <Text type="secondary">Add products to preview</Text>
                    </div>
                  )}

                  {/* Carousel Indicators */}
                  {products.length > 1 && (
                    <div className={styles.carouselIndicators}>
                      {products.map((_, index) => (
                        <div
                          key={index}
                          className={`${styles.indicator} ${
                            index === currentProductIndex ? styles.activeIndicator : ""
                          }`}
                          onClick={() => setCurrentProductIndex(index)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* TV Stand */}
              <div className={styles.tvStand} />
            </div>
          </div>
        </Col>
      </Row>

      {/* Export Modal */}
      <Modal
        title="Export TV Ad"
        open={exportModalVisible}
        onCancel={() => setExportModalVisible(false)}
        footer={null}
        width={500}
      >
        <div className={styles.exportModal}>
          <Text style={{ display: "block", marginBottom: 16 }}>
            Choose how you want to export your TV ad:
          </Text>

          <div className={styles.exportOptions}>
            <div
              className={styles.exportOption}
              onClick={() => {
                setExportModalVisible(false);
                startRecording();
              }}
            >
              <VideoCameraOutlined style={{ fontSize: 32, color: "#1890ff" }} />
              <div>
                <Text strong>Export as Video (WebM)</Text>
                <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                  Records the carousel animation as a video file
                </Text>
              </div>
            </div>

            <div
              className={styles.exportOption}
              onClick={() => {
                setExportModalVisible(false);
                exportFrames();
              }}
            >
              <FileImageOutlined style={{ fontSize: 32, color: "#52c41a" }} />
              <div>
                <Text strong>Export as Images (PNG)</Text>
                <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                  Downloads each product as a separate image
                </Text>
              </div>
            </div>

            <div
              className={styles.exportOption}
              onClick={() => {
                setExportModalVisible(false);
                exportConfig();
              }}
            >
              <DownloadOutlined style={{ fontSize: 32, color: "#722ed1" }} />
              <div>
                <Text strong>Export Configuration (JSON)</Text>
                <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                  Save settings for use with professional video tools
                </Text>
              </div>
            </div>
          </div>

          <Divider />

          <Text type="secondary" style={{ fontSize: 12 }}>
            Tip: For best quality MP4 output, export as images and use video editing software
            like Adobe Premiere, Final Cut Pro, or free tools like DaVinci Resolve.
          </Text>
        </div>
      </Modal>
    </div>
  );
}
